import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Play, CheckCircle, XCircle, AlertCircle, Clock,
  ChevronDown, ChevronRight, RefreshCw, Shield, Server,
  FileText, Users, Key
} from 'lucide-react';

const TEST_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  PASSED: 'passed',
  FAILED: 'failed',
  SKIPPED: 'skipped'
};

const createTestResult = (status, message, details = null, responseTime = null) => ({
  status, message, details, responseTime, timestamp: new Date().toISOString()
});

const useAPITestRunner = () => {
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const makeRequest = useCallback(async (endpoint, options = {}) => {
    const startTime = performance.now();
    const defaultHeaders = { 'Content-Type': 'application/json' };

    if (options.useAuth !== false && (options.token || token)) {
      defaultHeaders['Authorization'] = `Bearer ${options.token || token}`;
    }

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: { ...defaultHeaders, ...options.headers },
      });

      const endTime = performance.now();
      const responseTime = Math.round(endTime - startTime);

      let data = null;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try { data = await response.json(); } catch (e) { data = null; }
      }

      return {
        ok: response.ok, status: response.status, statusText: response.statusText,
        data, responseTime, headers: Object.fromEntries(response.headers.entries())
      };
    } catch (error) {
      const endTime = performance.now();
      return {
        ok: false, status: 0, statusText: 'Network Error',
        data: null, error: error.message, responseTime: Math.round(endTime - startTime)
      };
    }
  }, [token, API_URL]);

  return { makeRequest, API_URL };
};

const TestResult = ({ test, result, isExpanded, onToggle }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case TEST_STATUS.PASSED: return <CheckCircle className="h-5 w-5 text-green-500" />;
      case TEST_STATUS.FAILED: return <XCircle className="h-5 w-5 text-red-500" />;
      case TEST_STATUS.RUNNING: return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case TEST_STATUS.SKIPPED: return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default: return <Clock className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case TEST_STATUS.PASSED: return 'bg-green-50 border-green-200';
      case TEST_STATUS.FAILED: return 'bg-red-50 border-red-200';
      case TEST_STATUS.RUNNING: return 'bg-blue-50 border-blue-200';
      case TEST_STATUS.SKIPPED: return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-slate-50 border-slate-200';
    }
  };

  return (
    <div className={`border rounded-lg mb-2 ${getStatusBg(result?.status || TEST_STATUS.PENDING)}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center space-x-3">
          {getStatusIcon(result?.status || TEST_STATUS.PENDING)}
          <div>
            <span className="font-medium text-slate-800">{test.name}</span>
            <span className="ml-2 text-xs text-slate-500">{test.method} {test.endpoint}</span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {result?.responseTime && <span className="text-xs text-slate-500">{result.responseTime}ms</span>}
          {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </div>
      </div>
      {isExpanded && result && (
        <div className="px-3 pb-3 border-t border-slate-200 mt-2 pt-2">
          <div className="text-sm">
            <p className="text-slate-600">{result.message}</p>
            {result.details && (
              <pre className="mt-2 p-2 bg-slate-800 text-slate-100 rounded text-xs overflow-x-auto">
                {JSON.stringify(result.details, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TestCategory = ({ category, tests, results, isRunning, onRunCategory, expandedTests, toggleTest }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const passedCount = Object.values(results).filter(r => r?.status === TEST_STATUS.PASSED).length;
  const failedCount = Object.values(results).filter(r => r?.status === TEST_STATUS.FAILED).length;

  const getCategoryIcon = (name) => {
    switch (name) {
      case 'Authentication': return <Shield className="h-5 w-5" />;
      case 'Students': case 'Therapists': return <Users className="h-5 w-5" />;
      case 'Session Reports': return <FileText className="h-5 w-5" />;
      case 'Admin': return <Key className="h-5 w-5" />;
      case 'Security': return <Shield className="h-5 w-5" />;
      default: return <Server className="h-5 w-5" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow mb-4">
      <div className="flex items-center justify-between p-4 cursor-pointer border-b" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center space-x-3">
          <span className="text-slate-600">{getCategoryIcon(category.name)}</span>
          <div>
            <h3 className="font-semibold text-slate-800">{category.name}</h3>
            <p className="text-sm text-slate-500">{category.description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-green-600">{passedCount} passed</span>
            <span className="text-slate-400">|</span>
            <span className="text-red-600">{failedCount} failed</span>
            <span className="text-slate-400">|</span>
            <span className="text-slate-600">{tests.length} total</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onRunCategory(category.id); }}
            disabled={isRunning}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
          >
            {isRunning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            <span>Run</span>
          </button>
          {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
        </div>
      </div>
      {isExpanded && (
        <div className="p-4">
          {tests.map(test => (
            <TestResult key={test.id} test={test} result={results[test.id]} isExpanded={expandedTests[test.id]} onToggle={() => toggleTest(test.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

const APITestSuite = () => {
  const { token, user } = useAuth();
  const { makeRequest } = useAPITestRunner();
  
  const [testResults, setTestResults] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const [runningCategory, setRunningCategory] = useState(null);
  const [expandedTests, setExpandedTests] = useState({});
  const [testLog, setTestLog] = useState([]);

  const addLog = (message, type = 'info') => {
    setTestLog(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
  };

  const toggleTest = (testId) => {
    setExpandedTests(prev => ({ ...prev, [testId]: !prev[testId] }));
  };

  // Define test categories
  const testCategories = [
    {
      id: 'auth',
      name: 'Authentication',
      description: 'Test authentication endpoints and JWT validation',
      tests: [
        {
          id: 'auth-verify-valid', name: 'Verify Valid Token', method: 'GET', endpoint: '/auth/verify-token',
          run: async () => {
            const response = await makeRequest('/auth/verify-token');
            if (response.ok && response.data?.msg === 'Token is valid') {
              return createTestResult(TEST_STATUS.PASSED, 'Token verification successful', response.data, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Token verification failed', response.data, response.responseTime);
          }
        },
        {
          id: 'auth-verify-invalid', name: 'Reject Invalid Token', method: 'GET', endpoint: '/auth/verify-token',
          run: async () => {
            const response = await makeRequest('/auth/verify-token', { token: 'invalid_token_12345', useAuth: true });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Invalid token correctly rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Invalid token was not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'auth-verify-missing', name: 'Reject Missing Token', method: 'GET', endpoint: '/auth/verify-token',
          run: async () => {
            const response = await makeRequest('/auth/verify-token', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Missing token correctly rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Missing token was not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'auth-renew-valid', name: 'Renew Token (Valid)', method: 'POST', endpoint: '/auth/renew',
          run: async () => {
            const response = await makeRequest('/auth/renew', { method: 'POST' });
            if (response.ok && response.data?.token) {
              return createTestResult(TEST_STATUS.PASSED, 'Token renewal successful', { hasNewToken: true }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Token renewal failed', response.data, response.responseTime);
          }
        },
        {
          id: 'auth-renew-noauth', name: 'Renew Token (No Auth)', method: 'POST', endpoint: '/auth/renew',
          run: async () => {
            const response = await makeRequest('/auth/renew', { method: 'POST', useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated renewal rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unauthenticated renewal not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'auth-users-admin', name: 'List Users (Admin Only)', method: 'GET', endpoint: '/auth/users',
          run: async () => {
            const response = await makeRequest('/auth/users');
            if (user?.role === 'admin') {
              if (response.ok && Array.isArray(response.data?.users)) {
                return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.users.length} users`, { count: response.data.users.length }, response.responseTime);
              }
              return createTestResult(TEST_STATUS.FAILED, 'Failed to retrieve users', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) {
                return createTestResult(TEST_STATUS.PASSED, 'Non-admin correctly denied', { status: response.status }, response.responseTime);
              }
              return createTestResult(TEST_STATUS.FAILED, 'Non-admin access control failed', response.data, response.responseTime);
            }
          }
        },
        {
          id: 'auth-users-noauth', name: 'List Users (No Auth)', method: 'GET', endpoint: '/auth/users',
          run: async () => {
            const response = await makeRequest('/auth/users', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated request rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unauthenticated request not rejected', response.data, response.responseTime);
          }
        }
      ]
    },
    {
      id: 'students',
      name: 'Students',
      description: 'Test student management endpoints',
      tests: [
        {
          id: 'students-list', name: 'List All Students', method: 'GET', endpoint: '/students/',
          run: async () => {
            const response = await makeRequest('/students/');
            if (response.ok && Array.isArray(response.data?.students)) {
              return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.students.length} students`, { count: response.data.students.length }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to retrieve students', response.data, response.responseTime);
          }
        },
        {
          id: 'students-list-noauth', name: 'List Students (No Auth)', method: 'GET', endpoint: '/students/',
          run: async () => {
            const response = await makeRequest('/students/', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated request rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unauthenticated request not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'students-get-valid', name: 'Get Student (Valid ID)', method: 'GET', endpoint: '/students/{id}',
          run: async () => {
            const listResponse = await makeRequest('/students/');
            if (!listResponse.ok || !listResponse.data?.students?.length) {
              return createTestResult(TEST_STATUS.SKIPPED, 'No students available', null, listResponse.responseTime);
            }
            const studentId = listResponse.data.students[0].id;
            const response = await makeRequest(`/students/${studentId}`);
            if (response.ok && response.data?.id === studentId) {
              return createTestResult(TEST_STATUS.PASSED, 'Student details retrieved', { studentId }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to get student', response.data, response.responseTime);
          }
        },
        {
          id: 'students-get-invalid', name: 'Get Student (Invalid ID)', method: 'GET', endpoint: '/students/99999999',
          run: async () => {
            const response = await makeRequest('/students/99999999');
            if (!response.ok && response.status === 404) {
              return createTestResult(TEST_STATUS.PASSED, 'Invalid ID returns 404', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Invalid ID handling incorrect', response.data, response.responseTime);
          }
        },
        {
          id: 'students-get-noauth', name: 'Get Student (No Auth)', method: 'GET', endpoint: '/students/1',
          run: async () => {
            const response = await makeRequest('/students/1', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated request rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unauthenticated request not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'students-assignments', name: 'Get Assignments (Role Check)', method: 'GET', endpoint: '/students/{id}/assignments',
          run: async () => {
            const listResponse = await makeRequest('/students/');
            if (!listResponse.ok || !listResponse.data?.students?.length) {
              return createTestResult(TEST_STATUS.SKIPPED, 'No students available', null, listResponse.responseTime);
            }
            const studentId = listResponse.data.students[0].id;
            const response = await makeRequest(`/students/${studentId}/assignments`);
            if (user?.role === 'admin' || user?.role === 'manager') {
              if (response.ok) return createTestResult(TEST_STATUS.PASSED, 'Assignments retrieved', response.data, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Manager/admin should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Therapist denied access', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        }
      ]
    },
    {
      id: 'therapists',
      name: 'Therapists',
      description: 'Test therapist management endpoints',
      tests: [
        {
          id: 'therapists-profile', name: 'Get Own Profile', method: 'GET', endpoint: '/therapists/profile',
          run: async () => {
            const response = await makeRequest('/therapists/profile');
            if (response.ok && response.data?.email) {
              return createTestResult(TEST_STATUS.PASSED, 'Profile retrieved', { email: response.data.email }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to get profile', response.data, response.responseTime);
          }
        },
        {
          id: 'therapists-profile-noauth', name: 'Get Profile (No Auth)', method: 'GET', endpoint: '/therapists/profile',
          run: async () => {
            const response = await makeRequest('/therapists/profile', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated request rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unauthenticated request not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'therapists-my-students', name: 'Get My Students', method: 'GET', endpoint: '/therapists/my-students',
          run: async () => {
            const response = await makeRequest('/therapists/my-students');
            if (response.ok && Array.isArray(response.data?.students)) {
              return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.students.length} students`, { count: response.data.students.length }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to get students', response.data, response.responseTime);
          }
        },
        {
          id: 'therapists-my-sessions', name: 'Get My Sessions', method: 'GET', endpoint: '/therapists/my-sessions',
          run: async () => {
            const response = await makeRequest('/therapists/my-sessions');
            if (response.ok && Array.isArray(response.data?.sessions)) {
              return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.sessions.length} sessions`, { count: response.data.sessions.length }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to get sessions', response.data, response.responseTime);
          }
        },
        {
          id: 'therapists-list', name: 'Get Therapists', method: 'GET', endpoint: '/therapists',
          run: async () => {
            const response = await makeRequest('/therapists');
            if (response.ok && Array.isArray(response.data?.therapists)) {
              return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.therapists.length} therapists`, { count: response.data.therapists.length }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to get therapists', response.data, response.responseTime);
          }
        },
        {
          id: 'therapists-list-noauth', name: 'Get Therapists (No Auth)', method: 'GET', endpoint: '/therapists',
          run: async () => {
            const response = await makeRequest('/therapists', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated request rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unauthenticated request not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'therapists-admin-list', name: 'Admin List (Role Check)', method: 'GET', endpoint: '/therapists/list',
          run: async () => {
            const response = await makeRequest('/therapists/list');
            if (user?.role === 'admin') {
              if (response.ok) return createTestResult(TEST_STATUS.PASSED, 'Admin list retrieved', { count: response.data?.therapists?.length }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Admin should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Non-admin denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        },
        {
          id: 'therapists-all', name: 'Get All (Manager/Admin)', method: 'GET', endpoint: '/therapists/all',
          run: async () => {
            const response = await makeRequest('/therapists/all');
            if (user?.role === 'admin' || user?.role === 'manager') {
              if (response.ok) return createTestResult(TEST_STATUS.PASSED, 'All therapists retrieved', { count: response.data?.therapists?.length }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Therapist denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        }
      ]
    },
    {
      id: 'session-reports',
      name: 'Session Reports',
      description: 'Test session report endpoints',
      tests: [
        {
          id: 'reports-list', name: 'List Reports', method: 'GET', endpoint: '/session-reports/list',
          run: async () => {
            const response = await makeRequest('/session-reports/list');
            if (response.ok && Array.isArray(response.data?.reports)) {
              return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.reports.length} reports`, { count: response.data.reports.length }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to get reports', response.data, response.responseTime);
          }
        },
        {
          id: 'reports-list-noauth', name: 'List Reports (No Auth)', method: 'GET', endpoint: '/session-reports/list',
          run: async () => {
            const response = await makeRequest('/session-reports/list', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated request rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unauthenticated request not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'reports-list-filtered', name: 'List Reports (Filtered)', method: 'GET', endpoint: '/session-reports/list?start=2024-01-01',
          run: async () => {
            const response = await makeRequest('/session-reports/list?start=2024-01-01&end=2025-12-31');
            if (response.ok && Array.isArray(response.data?.reports)) {
              return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.reports.length} filtered reports`, { count: response.data.reports.length }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Filter failed', response.data, response.responseTime);
          }
        },
        {
          id: 'reports-get-valid', name: 'Get Report (Valid ID)', method: 'GET', endpoint: '/session-reports/{id}',
          run: async () => {
            const listResponse = await makeRequest('/session-reports/list');
            if (!listResponse.ok || !listResponse.data?.reports?.length) {
              return createTestResult(TEST_STATUS.SKIPPED, 'No reports available', null, listResponse.responseTime);
            }
            const reportId = listResponse.data.reports[0].id;
            const response = await makeRequest(`/session-reports/${reportId}`);
            if (response.ok && response.data?.id === reportId) {
              return createTestResult(TEST_STATUS.PASSED, 'Report retrieved', { reportId }, response.responseTime);
            }
            if (response.status === 403) {
              return createTestResult(TEST_STATUS.PASSED, 'Access control working', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Failed to get report', response.data, response.responseTime);
          }
        },
        {
          id: 'reports-get-invalid', name: 'Get Report (Invalid ID)', method: 'GET', endpoint: '/session-reports/99999999',
          run: async () => {
            const response = await makeRequest('/session-reports/99999999');
            if (!response.ok && response.status === 404) {
              return createTestResult(TEST_STATUS.PASSED, 'Invalid ID returns 404', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Invalid ID handling incorrect', response.data, response.responseTime);
          }
        },
        {
          id: 'reports-students', name: 'Get Students (Role Check)', method: 'GET', endpoint: '/session-reports/students',
          run: async () => {
            const response = await makeRequest('/session-reports/students');
            if (user?.role === 'admin' || user?.role === 'manager') {
              if (response.ok) return createTestResult(TEST_STATUS.PASSED, 'Students list retrieved', { count: response.data?.students?.length }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Therapist denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        },
        {
          id: 'reports-payment', name: 'Update Payment (Role Check)', method: 'PUT', endpoint: '/session-reports/{id}/payment-status',
          run: async () => {
            const listResponse = await makeRequest('/session-reports/list');
            if (!listResponse.ok || !listResponse.data?.reports?.length) {
              return createTestResult(TEST_STATUS.SKIPPED, 'No reports available', null, listResponse.responseTime);
            }
            const reportId = listResponse.data.reports[0].id;
            const response = await makeRequest(`/session-reports/${reportId}/payment-status`, {
              method: 'PUT', body: JSON.stringify({ isPaid: false })
            });
            if (user?.role === 'admin' || user?.role === 'manager') {
              if (response.ok) return createTestResult(TEST_STATUS.PASSED, 'Payment update successful', null, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Therapist denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        }
      ]
    },
    {
      id: 'admin',
      name: 'Admin',
      description: 'Test admin-only endpoints',
      tests: [
        {
          id: 'admin-api-key', name: 'Get API Key (Admin)', method: 'GET', endpoint: '/admin/api-key',
          run: async () => {
            const response = await makeRequest('/admin/api-key');
            if (user?.role === 'admin') {
              if (response.ok) return createTestResult(TEST_STATUS.PASSED, 'API key retrieved', { hasKey: !!response.data?.key }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Admin should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Non-admin denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        },
        {
          id: 'admin-api-key-noauth', name: 'Get API Key (No Auth)', method: 'GET', endpoint: '/admin/api-key',
          run: async () => {
            const response = await makeRequest('/admin/api-key', { useAuth: false });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Unauthenticated rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Should reject unauthenticated', response.data, response.responseTime);
          }
        },
        {
          id: 'admin-alerts', name: 'Get Security Alerts (Admin)', method: 'GET', endpoint: '/admin/security/alerts',
          run: async () => {
            const response = await makeRequest('/admin/security/alerts');
            if (user?.role === 'admin') {
              if (response.ok && Array.isArray(response.data?.alerts)) {
                return createTestResult(TEST_STATUS.PASSED, `Retrieved ${response.data.alerts.length} alerts`, { count: response.data.alerts.length }, response.responseTime);
              }
              return createTestResult(TEST_STATUS.FAILED, 'Admin should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Non-admin denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        },
        {
          id: 'admin-extraction-logs', name: 'Get Extraction Logs (Admin)', method: 'GET', endpoint: '/admin/extraction-logs',
          run: async () => {
            const response = await makeRequest('/admin/extraction-logs');
            if (user?.role === 'admin') {
              if (response.ok && response.data?.logs !== undefined) {
                return createTestResult(TEST_STATUS.PASSED, 'Extraction logs retrieved', { count: response.data.logs?.length, stats: response.data.stats }, response.responseTime);
              }
              return createTestResult(TEST_STATUS.FAILED, 'Admin should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Non-admin denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        },
        {
          id: 'admin-extraction-overview', name: 'Get Extraction Overview (Admin)', method: 'GET', endpoint: '/admin/extraction-overview',
          run: async () => {
            const response = await makeRequest('/admin/extraction-overview');
            if (user?.role === 'admin') {
              if (response.ok && response.data?.overview) {
                return createTestResult(TEST_STATUS.PASSED, 'Overview retrieved', response.data.overview, response.responseTime);
              }
              return createTestResult(TEST_STATUS.FAILED, 'Admin should have access', response.data, response.responseTime);
            } else {
              if (!response.ok && response.status === 403) return createTestResult(TEST_STATUS.PASSED, 'Non-admin denied', { status: response.status }, response.responseTime);
              return createTestResult(TEST_STATUS.FAILED, 'Access control failed', response.data, response.responseTime);
            }
          }
        }
      ]
    },
    {
      id: 'security',
      name: 'Security',
      description: 'Test security controls and edge cases',
      tests: [
        {
          id: 'security-expired-token', name: 'Reject Expired Token', method: 'GET', endpoint: '/therapists/profile',
          run: async () => {
            const expiredToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ0ZXN0QHRlc3QuY29tIiwiZXhwIjoxNjAwMDAwMDAwfQ.invalid';
            const response = await makeRequest('/therapists/profile', { token: expiredToken });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Expired token rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Expired token not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'security-malformed-token', name: 'Reject Malformed Token', method: 'GET', endpoint: '/therapists/profile',
          run: async () => {
            const response = await makeRequest('/therapists/profile', { token: 'not.a.valid.jwt.token' });
            if (!response.ok && (response.status === 401 || response.status === 422)) {
              return createTestResult(TEST_STATUS.PASSED, 'Malformed token rejected', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Malformed token not rejected', response.data, response.responseTime);
          }
        },
        {
          id: 'security-sql-injection', name: 'SQL Injection Prevention', method: 'GET', endpoint: '/students/1; DROP TABLE--',
          run: async () => {
            const response = await makeRequest('/students/1; DROP TABLE Students;--');
            if (!response.ok && response.status !== 500) {
              return createTestResult(TEST_STATUS.PASSED, 'SQL injection handled safely', { status: response.status }, response.responseTime);
            }
            if (response.status === 500) {
              return createTestResult(TEST_STATUS.FAILED, 'Possible SQL injection vulnerability', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Unexpected response', response.data, response.responseTime);
          }
        },
        {
          id: 'security-xss', name: 'XSS Prevention', method: 'GET', endpoint: '/session-reports/list?start=<script>',
          run: async () => {
            const response = await makeRequest('/session-reports/list?start=<script>alert(1)</script>');
            if (response.status !== 500) {
              return createTestResult(TEST_STATUS.PASSED, 'XSS handled safely', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'XSS caused server error', { status: response.status }, response.responseTime);
          }
        },
        {
          id: 'security-path-traversal', name: 'Path Traversal Prevention', method: 'GET', endpoint: '/students/../../../etc/passwd',
          run: async () => {
            const response = await makeRequest('/students/../../../etc/passwd');
            if (!response.ok) {
              return createTestResult(TEST_STATUS.PASSED, 'Path traversal blocked', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.PASSED, 'Path traversal handled', { status: response.status }, response.responseTime);
          }
        }
      ]
    },
    {
      id: 'error-handling',
      name: 'Error Handling',
      description: 'Test error responses and graceful degradation',
      tests: [
        {
          id: 'error-404', name: 'Non-existent Endpoint', method: 'GET', endpoint: '/nonexistent/endpoint',
          run: async () => {
            const response = await makeRequest('/nonexistent/endpoint/12345');
            if (!response.ok && response.status === 404) {
              return createTestResult(TEST_STATUS.PASSED, 'Returns 404', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Should return 404', { status: response.status }, response.responseTime);
          }
        },
        {
          id: 'error-invalid-json', name: 'Invalid JSON Body', method: 'POST', endpoint: '/auth/users',
          run: async () => {
            try {
              const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/users`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: 'not valid json {'
              });
              if (!response.ok && (response.status === 400 || response.status === 422)) {
                return createTestResult(TEST_STATUS.PASSED, 'Invalid JSON rejected', { status: response.status }, 0);
              }
              return createTestResult(TEST_STATUS.FAILED, 'Invalid JSON not rejected', { status: response.status }, 0);
            } catch (error) {
              return createTestResult(TEST_STATUS.PASSED, 'Invalid JSON handled', { error: error.message }, 0);
            }
          }
        },
        {
          id: 'error-wrong-method', name: 'Wrong HTTP Method', method: 'DELETE', endpoint: '/students/',
          run: async () => {
            const response = await makeRequest('/students/', { method: 'DELETE' });
            if (!response.ok && (response.status === 405 || response.status === 404)) {
              return createTestResult(TEST_STATUS.PASSED, 'Wrong method rejected', { status: response.status }, response.responseTime);
            }
            if (!response.ok) {
              return createTestResult(TEST_STATUS.PASSED, 'Method handled', { status: response.status }, response.responseTime);
            }
            return createTestResult(TEST_STATUS.FAILED, 'Should reject wrong method', { status: response.status }, response.responseTime);
          }
        }
      ]
    }
  ];

  // Run a single test
  const runTest = async (test) => {
    setTestResults(prev => ({ ...prev, [test.id]: createTestResult(TEST_STATUS.RUNNING, 'Running...') }));
    try {
      const result = await test.run();
      setTestResults(prev => ({ ...prev, [test.id]: result }));
      return result;
    } catch (error) {
      const result = createTestResult(TEST_STATUS.FAILED, `Test error: ${error.message}`, { error: error.stack });
      setTestResults(prev => ({ ...prev, [test.id]: result }));
      return result;
    }
  };

  // Run all tests in a category
  const runCategory = async (categoryId) => {
    setIsRunning(true);
    setRunningCategory(categoryId);
    addLog(`Starting tests for: ${categoryId}`, 'info');

    const category = testCategories.find(c => c.id === categoryId);
    if (!category) return;

    for (const test of category.tests) {
      addLog(`Running: ${test.name}`, 'info');
      const result = await runTest(test);
      addLog(`${test.name}: ${result.status}`, result.status === TEST_STATUS.PASSED ? 'success' : 'error');
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setIsRunning(false);
    setRunningCategory(null);
    addLog(`Completed: ${categoryId}`, 'info');
  };

  // Run all tests
  const runAllTests = async () => {
    setIsRunning(true);
    addLog('Starting all tests...', 'info');
    for (const category of testCategories) {
      setRunningCategory(category.id);
      for (const test of category.tests) {
        await runTest(test);
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    setRunningCategory(null);
    setIsRunning(false);
    addLog('All tests completed!', 'info');
  };

  // Calculate statistics
  const getStatistics = () => {
    const allTests = testCategories.flatMap(c => c.tests);
    const total = allTests.length;
    const passed = Object.values(testResults).filter(r => r?.status === TEST_STATUS.PASSED).length;
    const failed = Object.values(testResults).filter(r => r?.status === TEST_STATUS.FAILED).length;
    const skipped = Object.values(testResults).filter(r => r?.status === TEST_STATUS.SKIPPED).length;
    return { total, passed, failed, skipped, pending: total - passed - failed - skipped };
  };

  const stats = getStatistics();

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">API Test Suite</h1>
            <p className="text-sm text-slate-500 mt-1">Comprehensive endpoint testing for security and functionality</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-slate-600">
              Role: <span className="font-semibold text-blue-600">{user?.role || 'Unknown'}</span>
            </div>
            <button
              onClick={runAllTests}
              disabled={isRunning}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {isRunning ? <><RefreshCw className="h-5 w-5 animate-spin" /><span>Running...</span></> : <><Play className="h-5 w-5" /><span>Run All Tests</span></>}
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-3xl font-bold text-slate-800">{stats.total}</div>
          <div className="text-sm text-slate-500">Total</div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4 text-center border border-green-200">
          <div className="text-3xl font-bold text-green-600">{stats.passed}</div>
          <div className="text-sm text-green-700">Passed</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4 text-center border border-red-200">
          <div className="text-3xl font-bold text-red-600">{stats.failed}</div>
          <div className="text-sm text-red-700">Failed</div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4 text-center border border-yellow-200">
          <div className="text-3xl font-bold text-yellow-600">{stats.skipped}</div>
          <div className="text-sm text-yellow-700">Skipped</div>
        </div>
        <div className="bg-slate-50 rounded-lg shadow p-4 text-center border border-slate-200">
          <div className="text-3xl font-bold text-slate-600">{stats.pending}</div>
          <div className="text-sm text-slate-500">Pending</div>
        </div>
      </div>

      {/* Role Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900">Role-Based Testing</h3>
            <p className="text-sm text-blue-700 mt-1">
              Tests adapt based on your role ({user?.role}). Admin endpoints verify denial for non-admins, and proper access for admins.
            </p>
          </div>
        </div>
      </div>

      {/* Test Categories */}
      <div className="space-y-4">
        {testCategories.map(category => (
          <TestCategory
            key={category.id}
            category={category}
            tests={category.tests}
            results={Object.fromEntries(category.tests.map(t => [t.id, testResults[t.id]]))}
            isRunning={isRunning && runningCategory === category.id}
            onRunCategory={runCategory}
            expandedTests={expandedTests}
            toggleTest={toggleTest}
          />
        ))}
      </div>

      {/* Test Log */}
      {testLog.length > 0 && (
        <div className="mt-6 bg-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white font-semibold">Test Log</h3>
            <button onClick={() => setTestLog([])} className="text-slate-400 hover:text-white text-sm">Clear</button>
          </div>
          <div className="max-h-48 overflow-y-auto font-mono text-xs">
            {testLog.map((log, i) => (
              <div key={i} className={`py-1 ${log.type === 'error' ? 'text-red-400' : log.type === 'success' ? 'text-green-400' : 'text-slate-300'}`}>
                <span className="text-slate-500">[{new Date(log.timestamp).toLocaleTimeString()}]</span> {log.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-6 text-center text-sm text-slate-500">
        <p>Testing {stats.total} endpoints across {testCategories.length} categories. Validates both functionality and security controls.</p>
      </div>
    </div>
  );
};

export default APITestSuite;
